import { Box, CircularProgress, Container, Grid, IconButton, Tooltip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import ScreenBanner from "../../../../../components/ScreenBanner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import useImprimirArquivo from "../../../../../lib/useImprimirArquivo";
import { handleUpdateObjectState } from "../../../../../helpers/handlersGenericos";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { Moment } from "moment";
import { IRecibos } from "../../../../../types/api/financeiro/pesquisar-recibos";
import tratativaDeDatas from "../../../../../helpers/tratativaDeDatas";
import DialogSolicitarCancelamentoRecibo from "./DialogSolicitarCancelamentoRecibo";
import DeleteIcon from "@mui/icons-material/Delete";
import DialogCancelarRecibo from "./DialogCancelarRecibo";
import { useModalDetalheProcesso } from "../../../../../components/ModalContexts/ModalDetalheProcessoContext";
import FiltroModal from "./components/FiltroModal";
import { IDataPublicos } from "../../../../../types/api/geral/pesquisar-publicos";
import PermissionGuard, { usePermission } from "../../../../../components/PermissionGuard";
import CancelIcon from '@mui/icons-material/Cancel';
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import ChipList from "../../../../../components/BasicComponents/ChipList";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";

export default function ListarRecibos() {
  const imprimirArquivo = useImprimirArquivo();
  const { openModal } = useModalDetalheProcesso();

  const [numRecibo, setNumRecibo] = useState<number>(0);
  const [processo, setProcesso] = useState<string | null>(null);
  const [openRequestCancelRecibo, setOpenRequestCancelRecibo] = useState<boolean>(false);
  const [openCancelRecibo, setOpenCancelRecibo] = useState<boolean>(false);

  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [filters, setFilters] = useState<TDialogFilters>(defaultDialogFilterState);
  const [palavraChave, setPalavraChave] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const searchTerm = useDebounce(palavraChave);

  //queries e mutations
  const paramsQuery = {
    data_recibo_inicio: filters.data_recibo_inicio?.format("YYYY-MM-DD"),
    data_recibo_fim: filters.data_recibo_fim?.format("YYYY-MM-DD"),
    ...(filters.publico
      ? { tipo_publico_contem: [filters.publico.tipo_publico] }
      : {}),
    ...(filters.publico
      ? { codigo_publico_contem: [filters.publico.codigo_publico] }
      : {}),
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["pesquisar-recibos", filters, searchTerm],
    queryFn: () =>
      axiosClient.get("/financeiro/contas-receber/pesquisar-recibos", {
        params: {
          limit: filters.porPagina,
          page: filters.pagina + 1,
          ...paramsQuery,
          numero_recibo_pesquisar: searchTerm || null,
        },
      }),
  });

  const { reqRecibos, dataRecibos }: IRecibos = useMemo(
    () => ({
      reqRecibos: data?.data,
      dataRecibos: data?.data.data || [],
    }),
    [data]
  );

  //Funções manipuladoras
  const handleChangePage = useCallback(
    (_: React.MouseEvent<HTMLButtonElement> | null, novaPag: number) => {
      handleUpdateObjectState("pagina", novaPag, setFilters);
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFilters((prevState) => ({
        ...prevState,
        porPagina: parseInt(event.target.value, 10),
        pagina: 0,
      }));
    },
    []
  );

  const atualizarListaRecibos = () => {
    refetch();
  };

  const handleFilterSubmit = (form: TDialogFilters) => {
    setFilters(form);
  };

  const handleImprimir = (numRecibo: number) => {
    imprimirArquivo(
      "financeiro/contas-receber/imprimir-recibo/" +
      numRecibo +
      "?segunda_via=true"
    );
  };

  const handleSearch = useCallback(() => {
    setFilters(prevState => ({
      ...prevState,
      pagina: 0
    }));
  }, []);

  const handleDeleteFilter = (item: string) => {
    const newFiltros = { ...filters };

    if (item.startsWith('Público:') && newFiltros.publico) {
      delete (newFiltros as any).publico;
    } 

    if (item.startsWith('Data recibo início:')) {
      newFiltros.data_recibo_inicio = null;
    }

    if (item.startsWith('Data recibo fim:')) {
      newFiltros.data_recibo_fim = null;
    }
    setFilters(newFiltros);
  }
  //
  useEffect(() => {
    if (processo) {
      openModal(processo);
      setProcesso(null);
    }
  }, [processo, openModal]);

  const chipItems = useMemo(() => {
    return getChipFilterItems(
      filters,
      filtersChipLabels,
      ['pagina', 'porPagina']
    );
  }, [filters]);

  //permissões
  const hasPermissionCancelarRecibo = usePermission('financeiro.contas-receber-cancelar-recibo');
  const hasPermissionSolicitarCancelamentoRecibo = usePermission('financeiro.contas-receber-solicitar-cancelamento-recibo');

  return (
    <>
      <ScreenBanner title="Financeiro" />
      <PermissionGuard permissionId="financeiro.listar-recibos">

        <Box sx={{ background: "#F5F6FA", minHeight: "100vh" }}>
          <Container sx={{ mt: 5 }}>
            <Typography textAlign="center" variant="h5" mb={3}>
              Listar recibos
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid size={{ xs: 12 }}>
                <Box component={Paper} p={2}>
                  <SearchWithFilters
                    palavraChave={palavraChave}
                    textFieldProps={{
                      inputRef: inputRef,
                      type: "number",
                      label: "Pesquisar",
                      placeholder: "Pesquise por número do recibo",
                      size: "medium",
                    }}
                    pesquisar={handleSearch}
                    setTermoPesquisa={(value) => setPalavraChave(value)}
                    handleOpenFilters={setOpenFilterModal}
                  />
                  <ChipList
                    items={chipItems}
                    onDelete={handleDeleteFilter}
                  />
                  {isFetching && (
                    <Box display="flex" justifyContent="center" my={4}>
                      <CircularProgress />
                    </Box>
                  )}
                  {!isFetching && (
                    <TableContainer>
                      <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                          <TableRow>
                            <TableCell align="left">
                              <b>Número</b>
                            </TableCell>
                            <TableCell align="left">
                              <b>Data</b>
                            </TableCell>
                            <TableCell align="left">
                              <b>Público</b>
                            </TableCell>
                            <TableCell align="left">
                              <b>Usuário</b>
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        {dataRecibos && dataRecibos.length > 0 && (
                          <TableBody>
                            {dataRecibos.map((recibo, index) => {
                              return (
                                <TableRow
                                  key={index}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell
                                    align="left"
                                    component="th"
                                    scope="row"
                                  >
                                    {recibo.numero_recibo}
                                  </TableCell>

                                  <TableCell
                                    align="left"
                                    component="th"
                                    scope="row"
                                  >
                                    {tratativaDeDatas(recibo.data_recibo)}
                                  </TableCell>

                                  <TableCell
                                    align="left"
                                    component="th"
                                    scope="row"
                                  >
                                    {recibo.publico}
                                  </TableCell>

                                  <TableCell
                                    align="left"
                                    component="th"
                                    scope="row"
                                  >
                                    {recibo.usuario}
                                  </TableCell>

                                  <TableCell>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="flex-end"
                                    >
                                      <Tooltip title="Imprimir recibo">
                                        <IconButton
                                          aria-label="edit"
                                          onClick={() =>
                                            handleImprimir(recibo.numero_recibo)
                                          }
                                        >
                                          <PrintIcon />
                                        </IconButton>
                                      </Tooltip>

                                      {hasPermissionSolicitarCancelamentoRecibo && (
                                        <Tooltip title="Solicitar cancelamento de recibo">
                                          <IconButton
                                            aria-label="solicitar cancelamento"
                                            onClick={() => {
                                              setOpenRequestCancelRecibo(true);
                                              setNumRecibo(recibo.numero_recibo);
                                            }}
                                          >
                                            <CancelIcon />
                                          </IconButton>
                                        </Tooltip>
                                      )}

                                      {hasPermissionCancelarRecibo && (
                                        <Tooltip title="Cancelar recibo">
                                          <IconButton
                                            aria-label="cancelar"
                                            onClick={() => {
                                              setOpenCancelRecibo(true);
                                              setNumRecibo(recibo.numero_recibo);
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                  )}

                  <TablePagination
                    component="div"
                    count={reqRecibos?.total ?? 1}
                    page={filters.pagina}
                    onPageChange={handleChangePage}
                    rowsPerPage={filters.porPagina}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Linhas por página"
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        {openRequestCancelRecibo && (
          <DialogSolicitarCancelamentoRecibo
            open={openRequestCancelRecibo}
            recibo={numRecibo}
            handleChangeProcesso={(item: string) => setProcesso(item)}
            handleClose={() => setOpenRequestCancelRecibo(false)}
          />
        )}
        {openCancelRecibo && (
          <DialogCancelarRecibo
            handleChangeProcesso={(item: string) => setProcesso(item)}
            open={openCancelRecibo}
            handleClose={() => setOpenCancelRecibo(false)}
            recibo={numRecibo}
            atualizarListaRecibos={atualizarListaRecibos}
          />
        )}
        {openFilterModal && (
          <FiltroModal
            {...{ filters }}
            open={openFilterModal}
            handleClose={() => setOpenFilterModal(false)}
            handleFilterSubmit={(form) => {
              handleFilterSubmit(form);
            }}
            onClearFilters={() => {
              setFilters(defaultDialogFilterState);
            }}
          />
        )}
      </PermissionGuard>
    </>
  );
}

export type TDialogFilters = {
  data_recibo_inicio: Moment | null;
  data_recibo_fim: Moment | null;
  publico: IDataPublicos | null;
  pagina: number;
  porPagina: number;
};

const defaultDialogFilterState = {
  data_recibo_inicio: null,
  data_recibo_fim: null,
  publico: null,
  pagina: 0,
  porPagina: 10,
};

const filtersChipLabels: { [key in keyof Omit<TDialogFilters, 'pagina' | 'porPagina'>]: string } = {
  data_recibo_inicio: 'Data recibo início',
  data_recibo_fim: 'Data recibo fim',
  publico: 'Público',
};
